import { SignalServiceBase } from "cayo.ui";
import { assignQuery, newQuery } from "@cayo/shared/components/GridContainer/gridModel/Query";
import {
  IQuery,
  GridControllerAction,
  IGridState,
} from "@cayo/shared/components/GridContainer/types";
import { UIInteraction } from "@cayo/shared/hooks/useUIInteraction";
import logger from "@cayo/shared/libs/logger";
import { urlUtils } from "@cayo/shared/utils/url";
import { GridContainerProps, GridController } from "./grid-model/GridController";
import { AdminItemsStore } from "./repo/AdminItemsStore";
import { AdminUserSettingsStore } from "./repo/AdminUserSettingsStore";
import { GridNotification } from "./repo/GridNotification";
import { ItemsStore } from "./repo/ItemsStore";
import { QueryStore, QueryStoreConfig } from "./repo/QueryStore";
import { UserSettingsStore } from "./repo/UserSettingsStore";
import { IContentScheme } from "../../api/schema.api";
import { APP_IDS, AppId } from "../../utils/app-ids";

const log = logger.getLogger("QUICK-FILTER-NEW createGridController.factory");

function dispatchOuterStub(action: GridControllerAction) {
  log.debug("dispatchOuter -> action", action);
}

export function createController(
  props: GridContainerProps,
  uiInteraction: UIInteraction,
  appId: AppId | null
) {
  const { scheme, gridSettingsKey } = props;
  const dl = scheme.detailsList;
  log.debug("details-list component", dl);
  log.debug("create controller -> props", props);
  if (!dl) {
    throw new Error("DetailsList not found");
  }
  const savedQueryConfig = getQueryConfig(scheme);

  const columns = (dl.columns ?? []).filter((c) => !!c);
  const url = dl.url ?? "";
  if (!url) {
    throw new Error("Url for load items not exists");
  }

  const initialState: IGridState = {
    id: dl.id!,
    columns: [],
    ajustColumnsToken: "",
    searchResult: { items: [], skipToken: null },
    loading: true,
    query: createQuery(scheme),
    savedQueries: [],
    recentQueries: [],
    showQueryPicker: false,
    showChooseColumns: false,
    selectedItems: [],
    hasChanges: false,
    gridSettingsKey,
    queryMode: "normal",
  };

  return new GridController(
    {
      allColumns: columns,
      initialQueryId: scheme.savedQueries?.initialQueryId,
      defaultGroupBy: scheme.detailsList?.groupBy ?? "",
      onSelect: props.onSelect,
      dispatchOuter: props.dispatchOuter ? props.dispatchOuter : dispatchOuterStub,
      autoLoad: scheme.detailsList?.autoLoad !== false,
    },
    initialState,
    scheme.detailsList?.usePostMethod
      ? new AdminItemsStore(url, scheme.detailsList?.pageSize)
      : new ItemsStore(url, scheme.detailsList?.pageSize),
    new QueryStore(savedQueryConfig),
    appId === APP_IDS.GUARDIAN
      ? new UserSettingsStore(gridSettingsKey)
      : new AdminUserSettingsStore(gridSettingsKey),
    uiInteraction,
    new SignalServiceBase(),
    GridNotification.createFn(dl.notificationMode, dl.notificationSource)
  );
}

function createQuery(schema: IContentScheme): IQuery {
  const sortedColumn = schema.detailsList!.columns?.find((c) => c.isSorted === true);
  const groupBy = schema.detailsList?.groupBy ?? "";
  let result: IQuery = assignQuery(newQuery(), { groupBy });

  if (sortedColumn) {
    result = assignQuery(result, {
      orderBy: {
        field: sortedColumn.fieldName!,
        direction: sortedColumn.isSortedDescending === true ? "descending" : "ascending",
      },
    });
  }
  return result;
}

function getQueryConfig(schema: IContentScheme): QueryStoreConfig | undefined {
  if (!!schema.savedQueries?.picker?.path && !!schema.targetType) {
    return {
      targetType: schema.targetType,
      queryUrl: schema.savedQueries.picker.path,
      expand: schema.savedQueries.picker.expand,
      objectPath: urlUtils.trim(window.location.hash),
      allowSavedQueries: schema.filterBar?.showSavedQueries === true,
      favoritesEnabled: schema.filterBar?.favoritesEnabled ?? false,
    };
  }

  return undefined;
}
